import { RxSocketUrls } from '@Constants/CommonConstants';
import SimpleTmpl from '@Services/SimpleTmpl';
import RequestUrls from '@Constants/RequestUrlsConstants';
import MessageBroker from '@Services/MessageBroker';
import RxSocket from '@Services/RxSocket';
import SocketBroker from '@Services/SocketBroker';
import { Subject } from 'rxjs';
import get from 'lodash/get';
import assignIn from 'lodash/assignIn';

// WS unsubscribe funcs
let unsubscribeInfo;
let unsubscribeResult;
let unregisterOnConnect;
let unregisterOnClose;

// RxJS subjects
let _infoPrivateSubject;
let _resultPrivateSubject;
let _onClosePrivateSubject;

/**
 * @returns {object} Return a promise for WS subscribed.
 * @description Subscribe sockets.
 */
function setupWebSockets(channel, callback) {
  return SocketBroker.observe(RxSocketUrls.Export[channel], (data) => {
    if (!data || !callback) {
      return;
    }

    callback({ data });
  });
}

function subscribeSocketRelatedStuff() {
  unsubscribeInfo = setupWebSockets('info', (data) => _infoPrivateSubject.next(data));
  unsubscribeResult = setupWebSockets('result', (data) => _resultPrivateSubject.next(data));
}

function destroySocketRelatedStuff() {
  if (unsubscribeInfo) {
    unsubscribeInfo();
    unsubscribeInfo = null;
  }

  if (unsubscribeResult) {
    unsubscribeResult();
    unsubscribeResult = null;
  }
}

function destroy() {
  destroySocketRelatedStuff();

  if (unregisterOnConnect) {
    unregisterOnConnect();
    unregisterOnConnect = null;
  }

  if (unregisterOnClose) {
    unregisterOnClose();
    unregisterOnClose = null;
  }
}

/**
 * @param {string} path
 * @param {object} payload Payload object with user preferences for Export.
 * @description Send a new action for Export.
 */
function sendExportAction(path, payload) {
  Logger.info(`WS export request (${path}):`, payload);
  RxSocket.send(path, payload);
}

/**
 * @description Cancel a specific Export job.
 */
function cancelExportAction(path, jobId, payload = {}) {
  RxSocket.send(SimpleTmpl.format(path, { jobId }), payload);
}

/**
 * @description Restore export by jobId
 */
function restoreExportAction(jobId, payload = {}) {
  RxSocket.send(SimpleTmpl.format(RxSocketUrls.Export.restore, { jobId }), payload);
}

function init() {
  destroy();

  _infoPrivateSubject = new Subject();
  _resultPrivateSubject = new Subject();
  _onClosePrivateSubject = new Subject();

  if (!unsubscribeInfo && !unsubscribeResult) {
    subscribeSocketRelatedStuff();
  }

  unregisterOnConnect = RxSocket.onConnect(subscribeSocketRelatedStuff);
  unregisterOnClose = RxSocket.onClose((err) => {
    _onClosePrivateSubject.next(err);
    destroySocketRelatedStuff();
  });
}

/**
 * @returns {object} Return an object containing the Export formats.
 * @description Get all export formats for specific context.
 */
function getExportFormats(databaseId, context) {
  const payload = {
    databaseId,
    context,
    main: context,
  };

  if (context === 'tgitems' || context === 'dpitems') {
    assignIn(payload, {
      main: 'substances',
    });
  }

  return MessageBroker.send(RequestUrls.exportOutputFormats, payload, { cache: false });
}

/**
 * @param {object} payload Pass a payload object with params
 * @returns {object} Return an object containing the Datapoints.
 * @description Get all export formats for Synthesis Planner.
 */
function getDatapoints(payload) {
  return MessageBroker.send(RequestUrls.getOutputItems, payload);
}

/**
 * @returns {Object} Return a deferred object.
 * @description Generate defer and subscribe listeners to info and result channels.
 */
function generateRequestSubject(payload, cancelPath) {
  const subject = new Subject();

  let infoSubscription = _infoPrivateSubject.subscribe((response) => {
    if (get(response, 'data.info.requestId') !== payload.requestId) {
      return;
    }

    Logger.info('WS export progress response:', response);
    subject.next({ type: 'progress', data: response });
  });

  let resultSubscription = _resultPrivateSubject.subscribe((response) => {
    if (get(response, 'data.info.requestId') !== payload.requestId) {
      return;
    }

    Logger.info('WS export response:', response);
    subject.next({ type: 'success', data: response });
    subject.complete();
  });

  let onCloseSubscription = _onClosePrivateSubject.subscribe((err) => {
    subject.error(assignIn({}, err, { isConnectionError: true }));
    subject.complete();
  });

  // Add a cancel function
  subject.cancel = (exportJobId) => {
    if (exportJobId) {
      cancelExportAction(cancelPath, exportJobId, { requestId: payload.requestId });
    }

    if (onCloseSubscription) {
      onCloseSubscription.unsubscribe();
      onCloseSubscription = null;
    }

    if (!infoSubscription && !resultSubscription) {
      return;
    }

    infoSubscription.unsubscribe();
    infoSubscription = null;
    resultSubscription.unsubscribe();
    resultSubscription = null;

    subject.error(null);
  };

  return subject;
}

/**
 * @returns {object} Return a Subject.
 * @description Generic export
 */
function sendExportRequest(payload) {
  const subject = generateRequestSubject(payload, RxSocketUrls.Export.cancel);
  sendExportAction(RxSocketUrls.Export.output, payload);
  return subject;
}

/**
 * @returns {object} Return a Subject.
 * @description ChemQuote export
 */
function sendChemQuoteExportRequest(payload) {
  const subject = generateRequestSubject(payload, RxSocketUrls.ExportChemQuote.cancel);
  sendExportAction(RxSocketUrls.ExportChemQuote.output, payload);
  return subject;
}

/**
 * @returns {object} Return a Subject.
 * @description Generic restore
 */
function sendRestoreRequest(payload, restoreJobId) {
  const subject = generateRequestSubject(payload, RxSocketUrls.Export.cancel);
  restoreExportAction(restoreJobId, payload);
  return subject;
}

export { init, destroy, getExportFormats, sendExportRequest, sendChemQuoteExportRequest, sendRestoreRequest, getDatapoints };
